/**
 * logic.ts — the replay surface's pure functions (no React, no network).
 *
 * Everything the timeline/cursor machinery needs is here so it is
 * unit-testable without a backend: window math (which slice of a run to
 * fetch around a cursor), the overlay precompute fold, and step navigation
 * over a lazily-known tick set (a recording can have GAPS — dataset
 * appends are best-effort and latch off on failure — so the cursor always
 * snaps to a RECORDED step, never a fabricated one).
 */
import type { Graph, NodeId } from '../engine/types';
import type { DecisionEntry, OverlayState } from '../overlays/annotations';
import { deriveOverlays } from '../overlays/annotations';
import type { ReplayTickRow } from './types';

/** Ticks fetched per window request. 2000 balances scrub latency against
 *  request count; the backend route caps at 5000. */
export const REPLAY_WINDOW = 2000;

/** Anchor windows at the run's first step so window indexes are stable
 *  regardless of which cursor triggered the fetch. */
export function windowIndexFor(step: number, firstStep: number): number {
  return Math.floor((step - firstStep) / REPLAY_WINDOW);
}

/** Inclusive [from, to] step bounds of one window. */
export function windowBounds(
  firstStep: number,
  index: number,
): { from: number; to: number } {
  const from = firstStep + index * REPLAY_WINDOW;
  return { from, to: from + REPLAY_WINDOW - 1 };
}

/** The hysteresis state threaded between consecutive ticks (and across
 *  sequentially-loaded windows during playback). */
export interface OverlayCarry {
  counts: Map<NodeId, number>;
  decisions: DecisionEntry[];
}

/** Fold `deriveOverlays` over a loaded run of ticks, producing the
 *  per-tick `OverlayState` a scrubber indexes into. Bottleneck hysteresis
 *  (N consecutive ticks) needs ticks IN ORDER; when the fold starts
 *  mid-run (a scrub jumped straight to a later window), pass that window's
 *  rows alone — the hysteresis resets at the window boundary, a documented
 *  approximation, and `carry` lets sequential playback thread the true
 *  state across windows. */
export function precomputeOverlays(
  rows: ReplayTickRow[],
  graph: Graph,
  carry?: OverlayCarry,
): { states: OverlayState[]; carry: OverlayCarry } {
  let counts = carry?.counts ?? new Map<NodeId, number>();
  let decisions = carry?.decisions ?? [];
  const states: OverlayState[] = [];
  for (const row of rows) {
    const r = deriveOverlays(row.tick, graph, counts, {
      prevDecisions: decisions,
    });
    states.push(r.state);
    counts = r.counts;
    decisions = r.state.decisions;
  }
  return { states, carry: { counts, decisions } };
}

/** Index of `target` in a sorted step array, or the insertion point
 *  (`steps.length` when past the end). */
export function stepIndex(steps: number[], target: number): number {
  let lo = 0;
  let hi = steps.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (steps[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** The closest RECORDED step to `target` (null when none are loaded). A
 * scrub into a gap lands on the nearest real tick, not a blank frame. */
export function nearestStep(steps: number[], target: number): number | null {
  if (steps.length === 0) return null;
  const i = stepIndex(steps, target);
  if (i >= steps.length) return steps[steps.length - 1];
  if (steps[i] === target) return target;
  const prev = steps[i - 1];
  if (prev === undefined) return steps[i];
  return Math.abs(target - prev) <= Math.abs(steps[i] - target) ? prev : steps[i];
}

/** First recorded step strictly after `target` (the ▶ button; null at the
 *  end of what is loaded). */
export function stepAfter(steps: number[], target: number): number | null {
  const i = stepIndex(steps, target + 1);
  return i < steps.length ? steps[i] : null;
}

/** Last recorded step strictly before `target` (the ◀ button). */
export function stepBefore(steps: number[], target: number): number | null {
  const i = stepIndex(steps, target) - 1;
  return i >= 0 ? steps[i] : null;
}

/** Move the cursor by `delta` RECORDED ticks (±1 per key press,
 *  ±10 with Shift), clamped to `range`. Null when the move would leave the
 *  LOADED steps — the caller loads the neighbouring window and retries. */
export function stepByRecorded(
  steps: number[],
  cursor: number,
  delta: number,
  range: { from: number; to: number },
): number | null {
  if (steps.length === 0) return null;
  const i = nearestIndex(steps, cursor);
  if (i === null) return null;
  const next = i + delta;
  if (next < 0 || next >= steps.length) return null;
  const step = steps[next];
  return Math.max(range.from, Math.min(range.to, step));
}

function nearestIndex(steps: number[], target: number): number | null {
  const s = nearestStep(steps, target);
  if (s === null) return null;
  const i = stepIndex(steps, s);
  return steps[i] === s ? i : null;
}

/** Clamp a [from, to] range into the run's bounds, keeping from ≤ to. */
export function clampRange(
  range: { from: number; to: number },
  bounds: { first: number; last: number },
): { from: number; to: number } {
  const from = Math.max(bounds.first, Math.min(bounds.last, range.from));
  const to = Math.max(from, Math.min(bounds.last, range.to));
  return { from, to };
}

/** Position of `step` along the timeline track, as a 0..1 fraction. */
export function trackFraction(step: number, from: number, to: number): number {
  if (to <= from) return 0;
  return Math.max(0, Math.min(1, (step - from) / (to - from)));
}

/** The step at a 0..1 track fraction (the scrubber's inverse map). */
export function stepAtFraction(f: number, from: number, to: number): number {
  const c = Math.max(0, Math.min(1, f));
  return Math.round(from + c * (to - from));
}

/** A filesystem-safe, practically-unique doc id from a label: slug +
 *  compact timestamp (the run-id minting convention). */
export function mintReplayId(label: string): string {
  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const stamp = new Date().toISOString().replace(/[-:]/g, '').slice(2, 15);
  return `${slug || 'replay'}-${stamp}`;
}

/** The Record dialog's default run name — `Run {local date time}` (the
 *  display name stored into the dataset registry's meta.label). */
export function defaultRunName(d: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `Run ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
